import React, {useEffect, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import { Layout, Card, MainLoader, Heading, ExternalLink, FormFieldInputNew, useForm, Text, Button } from "@netapp/bxp-design-system-react";
import { isNcl } from 'utils/app/utils';
import styles from "./NoOrgDialog.module.scss";
import { setItemAnon } from "utils/localstorage";
import * as yup from 'yup';
import { appComponents as i18nNamespace } from "../../../i18n/namespace";
import { useTranslation } from "react-i18next";
import { ReactComponent as OrgImage } from './Organisation.svg';
import patterns from 'utils/patterns';

const useorgFormSchema= (t) => {
    return  yup.object().shape({
    orgName: yup
        .string()
        .required(t("organization_name_required"))
        .max(130, t("name_length"))
        .matches(patterns.scopeName.regex, t("name_validation_message"))
});
}

const getStartedUrl = 'https://docs.netapp.com/us-en/bluexp-setup-admin/task-iam-get-started.html'

export default React.memo(() => {
    const {isReloading, isCreatingAccount, userCompany, name, errMessage, cmatCompanyID, crmCompanyID, cmatCompanyName} = useSelector((state) => ({
                                                                            isReloading: state.app.isReloading,
                                                                            isCreatingAccount: state.tenancy?.creatingAccount,
                                                                            errMessage: state.tenancy?.errMessage,
                                                                            userCompany: state.tenancy?.user?.company,
                                                                            name: state.auth.firstName,
                                                                            cmatCompanyID: state.tenancy?.user?.cmatCompanyID,
                                                                            crmCompanyID: state.tenancy?.user?.crmCompanyID,
                                                                            cmatCompanyName: state.tenancy?.user?.cmatCompanyName
                                                                            }));
    const dispatch = useDispatch();
    const [isPending, setIsPending] = useState(true);
    const [submittedOrgName, setSubmittedOrgName] = useState();
    const {t} = useTranslation(i18nNamespace);
    const orgFormSchema = useorgFormSchema(t);
    const form = useForm({orgName: ''}, orgFormSchema);
    const { isValid, handleFormSubmit, formState, formErrors, dirty } = form;

    useEffect(() => {
        if (userCompany) {
            form.handleFormChange(userCompany, {name: 'orgName'});
        }
        setIsPending(false);
    }, [userCompany]);

    const orgErrMessage = formState.orgName === submittedOrgName ? errMessage : null;

    const createOrganization = () => {
        setSubmittedOrgName(formState.orgName);
        setItemAnon({itemKey: `dontShowNewBlue`}, true)
        const payload = {
            organizationName: formState.orgName,
            resourceType: "organization",
            resourceClass: "hierarchy",
            version: "1.0",
            type: "application/vnd.netapp.bxp.resource"
        };
        // Pass cmatCompanyID, crmCompanyID, cmatCompanyName If present in store
        if (cmatCompanyID) {
            payload.tags = [{ "internal:bxp:cmatId": cmatCompanyID }];
        }
        if(crmCompanyID) {
            payload.tags = [...(payload.tags || []), { "internal:bxp:crmId": crmCompanyID }];
        }
        if(cmatCompanyName) {
            payload.tags = [...(payload.tags || []), { "internal:bxp:cmatCompanyName": cmatCompanyName }];
        }
        
        dispatch({
            type: 'TENANCYV4:CREATE-ORGANIZATION',
            payload
        });
    };

    if(isReloading) {
        return <MainLoader/>
    }

    return <>
        <Layout.Page>
            <Layout.Content>
                <Layout.Container className={styles['layout-container']}>
                    <Layout.Grid centerContent>
                        <Layout.GridItem>
                            <div className={styles['centered-flex-container']}>
                                <OrgImage/>
                                <Card className={styles.cardBase}>
                                    <Heading level="24" bold='true'>{t("welcome")}, {name}</Heading>
                                    <br />
                                    <Text bold level="16">{t(isNcl() ? "create_organization_ncl" : "create_organization")}</Text>
                                    <br />
                
                                    <Text level="14">{t(isNcl() ? "top_level_element_ncl" : "top_level_element")}</Text>
                                    <br />
                                    <ExternalLink variant="text" href={getStartedUrl} includeArrow={true}>{t("learn_more_about_organizations")}</ExternalLink>
                                    <br /><br />
                                    <FormFieldInputNew
                                        name="orgName"
                                        label={t("organization_name")}
                                        disabled={isCreatingAccount}
                                        form={form}
                                        autoComplete="off"
                                        pending={isPending}
                                        showError={!!orgErrMessage || !!(dirty.orgName && formErrors.orgName)}
                                        errorMessage={orgErrMessage || formErrors.orgName}
                                    />

                                    <br/>
                                    {
                                        !isNcl() &&  <div className={styles.infoSection}>
                                            <Text level="14">{t("enable_key_workflows")}</Text>
                                        </div>
                                    }
                                    <Button className={isNcl() ? styles['create-button-ncl'] : styles['create-button']} disabled={!isValid} onClick={handleFormSubmit(createOrganization)} isSubmitting={isCreatingAccount}>{t("continue")}</Button>
                                </Card>
                            </div>
                        </Layout.GridItem>
                    </Layout.Grid>
                </Layout.Container>
            </Layout.Content>
        </Layout.Page>
    </>
})